import * as React from 'react';
import { 
  Typography, 
  Box, 
  Skeleton, 
  Button, 
  Chip, 
  Grid, 
  Paper, 
  IconButton,
  Stack,
  Divider,
  Card,
  CardContent,
  CardActions,
  Tooltip
} from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import moment from "moment";
import { isMobile, isIOS } from 'react-device-detect';
import DownloadIcon from '@mui/icons-material/Download';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AppleIcon from '@mui/icons-material/Apple';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import QRCode from "react-qr-code";
import { downloadTicket } from '../store/actions/userActions';
import { motion } from 'framer-motion';
import { marketingColors, marketingFonts } from './tickitMarketingTheme';

function TicketCard({ ticket, downloadTicket }) {
  const navigate = useNavigate();
  const [showQr, setShowQr] = React.useState(false);

  const getStatusChip = () => {
    if (ticket?.validated) {
      return {
        icon: <CheckCircleIcon />,
        label: "Used",
        color: "success"
      };
    } else if (ticket?.fulfilled) {
      return {
        icon: <CheckCircleIcon />,
        label: "Valid",
        color: "primary"
      };
    } else {
      return {
        icon: <ErrorIcon />,
        label: "Invalid",
        color: "error"
      };
    }
  };

  const status = getStatusChip();

  const handleAppleWalletDownload = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/wallet/applewallet?ticketId=${ticket._id}`);
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Error generating Apple Wallet pass:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      style={{ width: '100%' }}
    >
      <Card 
        elevation={0}
        sx={{ 
          width: '100%',
          borderRadius: '16px',
          backgroundColor: '#fff',
          border: `1px solid ${marketingColors.border}`,
          boxShadow: '0 2px 20px rgba(70,16,155,0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(70,16,155,0.12)',
            borderColor: marketingColors.p200,
          }
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Tooltip title={ticket?.event.title} placement="top">
                  <Typography 
                    variant="h6" 
                    fontWeight="bold" 
                    noWrap
                    sx={{ 
                      color: marketingColors.gray900,
                      fontFamily: marketingFonts.display,
                      fontWeight: 800,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {ticket?.event.title}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs="auto">
                <Chip 
                  icon={status.icon}
                  label={status.label}
                  color={status.color}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    minWidth: '80px',
                    '& .MuiChip-icon': {
                      fontSize: '1rem'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Ticket Holder: {ticket?.userInfo?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Category: {ticket?.category}
              </Typography>
              {ticket?.fulfilled && (
                <Typography variant="body2" color="text.secondary">
                  Ticket Nr: {ticket?._id}
                </Typography>
              )}
            </Stack>

            {showQr && (
              <>
                <Divider />
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  <QRCode
                    style={{ 
                      height: 'auto', 
                      maxWidth: '70%', 
                      width: '70%',
                      backgroundColor: 'white',
                      padding: '8px',
                      borderRadius: '4px'
                    }}
                    value={ticket?._id}
                    fgColor="#000000"
                    bgColor="#ffffff"
                  />
                </Box>
              </>
            )}
          </Stack>
        </CardContent>

        {ticket?.fulfilled && (
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Stack direction="row" spacing={1} width="100%" justifyContent="flex-end">
              <IconButton 
                onClick={() => setShowQr(!showQr)}
                sx={{ 
                  color: 'var(--primaryPurple)',
                  '&:hover': { backgroundColor: 'rgba(95, 53, 184, 0.04)' }
                }}
              >
                <QrCode2Icon />
              </IconButton>
              <IconButton 
                onClick={() => downloadTicket({ ticketId: ticket?._id })}
                sx={{ 
                  color: 'var(--primaryPurple)',
                  '&:hover': { backgroundColor: 'rgba(95, 53, 184, 0.04)' }
                }}
              >
                <DownloadIcon />
              </IconButton>
              {isIOS && (
                <Button
                  variant="outlined"
                  startIcon={<AppleIcon />}
                  onClick={handleAppleWalletDownload}
                  size="small"
                  sx={{ 
                    borderRadius: 1,
                    textTransform: 'none',
                    borderColor: 'var(--primaryPurple)',
                    color: 'var(--primaryPurple)',
                    '&:hover': {
                      borderColor: 'var(--secPurple)',
                      backgroundColor: 'rgba(95, 53, 184, 0.04)'
                    }
                  }}
                >
                  Add to Wallet
                </Button>
              )}
            </Stack>
          </CardActions>
        )}
      </Card>
    </motion.div>
  );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = { downloadTicket };

export default connect(mapStateToProps, mapDispatchToProps)(TicketCard);